import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner, Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, DialogActions, Button } from "@fluentui/react-components";
import { useProgressPageStyles } from "../styles/progressPageStyles";

import { Warning48Filled } from '@fluentui/react-icons';
import { ProgressPageProps, ProgressCallback } from "../types/ProgressTypes";

const ProgressPage: React.FC<ProgressPageProps> = ({
    fetchData,
    loadingMessage,
    successMessage,
    backTo,
    autoReturnDelay = 2000,
    onComplete, // Dodaj opcjonalny callback
    reportCode,
    reportName,
    eupName,
    additionalInfo
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [progressMessage, setProgressMessage] = useState(loadingMessage || t("progress_loading_default"));
    const [progressPercentage, setProgressPercentage] = useState<number | undefined>();
    const [countdown, setCountdown] = useState(Math.ceil(autoReturnDelay / 1000));
    const [resultMetadata, setResultMetadata] = useState<any>(null);

    // Stan dla potwierdzenia
    const [confirmationRequired, setConfirmationRequired] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [confirmationTitle, setConfirmationTitle] = useState("");
    const [confirmationResolve, setConfirmationResolve] = useState<((result: boolean) => void) | null>(null);

    // Stan dla dialogu (stary)
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogResolve, setDialogResolve] = useState<((result: boolean) => void) | null>(null);

    const styles = useProgressPageStyles();
    // const navigate = useNavigate(); // Removed - not needed when onComplete is provided

    // Funkcja obsługi potwierdzenia (nowa - inline w progress)
    const showConfirmDialog = (message: string, title = ""): Promise<boolean> => {
        return new Promise((resolve) => {
            setLoading(false);
            setConfirmationMessage(message);
            setConfirmationTitle(title || t("progress_confirmation_title"));
            setConfirmationRequired(true);
            setConfirmationResolve(() => resolve);
        });
    };

    const handleConfirmation = (result: boolean) => {
        setConfirmationRequired(false);
        if (confirmationResolve) {
            confirmationResolve(result);
            if (result) {
                // Jeśli użytkownik potwierdził, wznów operację
                setLoading(true);
                setProgressMessage(t("progress_continue_operation"));
            }
        }
        setConfirmationResolve(null);
    };

    const handleDialogConfirm = (result: boolean) => {
        setDialogOpen(false);
        if (dialogResolve) {
            dialogResolve(result);
        }
        setDialogResolve(null);
    };

    useEffect(() => {
        const callbacks: ProgressCallback = {
            onProgress: (message: string, percentage?: number) => {
                setProgressMessage(message);
                setProgressPercentage(percentage);
            },
            onError: (errorMessage: string) => {
                setError(errorMessage);
                setLoading(false);
            },
            onSuccess: (message: string, metadata?: any) => {
                console.log('📦 ProgressPage: onSuccess called with metadata:', metadata);
                setProgressMessage(message || successMessage || t("progress_success_default"));
                setLoading(false);
                setSuccess(true);
                if (metadata) {
                    console.log('✅ ProgressPage: Setting resultMetadata:', metadata);
                    setResultMetadata(metadata);
                } else {
                    console.warn('⚠️ ProgressPage: No metadata received in onSuccess');
                }
            },
            onConfirm: showConfirmDialog
        };

        fetchData(callbacks).catch((err) => {
            setError(err?.message || t("progress_error_default"));
            setLoading(false);
        });
    }, [fetchData, successMessage, t]);

    // Countdown timer dla auto return
    useEffect(() => {
        if (success && autoReturnDelay > 0) {
            const timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        // Przenieś wywołanie onComplete tutaj:
                        setTimeout(() => {
                            if (onComplete) {
                                console.log('🔄 ProgressPage: Calling onComplete with resultMetadata:', resultMetadata);
                                onComplete(true, resultMetadata); // Przekaż informację o sukcesie i metadata
                            } else {
                                console.warn('No onComplete callback provided and no navigation available');
                            }
                        }, 0);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
        return undefined;
    }, [success, autoReturnDelay, onComplete]);

    const handleManualReturn = () => {
        if (onComplete) {
            setTimeout(() => onComplete(success), 0); // Przekaż info czy był sukces
        } else {
            console.warn('No onComplete callback provided and no navigation available');
        }
    };

    if (confirmationRequired) {
        return (
            <div className={styles.root}>
                {/*<div className={styles.spacing} />*/}
                <div className={styles.progressContainer}>
                    <div className={styles.confirmationContainer}>
                        <Warning48Filled className={styles.iconError} />
                        <div className={styles.confirmationTitle}>{confirmationTitle}</div>
                        <div className={styles.confirmationMessage}>{confirmationMessage}</div>
                        <div className={styles.confirmationButtons}>
                            <button
                                className={styles.confirmationButtonNo}
                                onClick={() => handleConfirmation(false)}
                            >
                                Nie
                            </button>
                            <button
                                className={styles.confirmationButtonYes}
                                onClick={() => handleConfirmation(true)}
                            >
                                Tak
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={styles.root}>
                {/*<div className={styles.spacing} />*/}
                <div className={styles.progressContainer}>
                    <Spinner size="huge" className={styles.iconLoading} />
                    <div className={styles.progressLoadingMessage}>{progressMessage}</div>
                    {progressPercentage !== undefined && (
                        <div className={styles.progressBar}>
                            <div
                                className={styles.progressBarFill}
                                ref={(el) => {
                                    if (el) {
                                        el.style.setProperty('--progress-width', `${progressPercentage}%`);
                                    }
                                }}
                            />
                            <span className={styles.progressText}>{progressPercentage}%</span>
                        </div>
                    )}
                    {/* Informacje kontekstowe */}
                    {(reportCode || reportName || eupName || additionalInfo) && (
                        <div style={{ marginTop: '16px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                            {(reportCode || reportName) && (
                                <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <strong style={{ marginRight: '8px' }}>Raport:</strong>
                                    {reportCode && (
                                        <span style={{
                                            display: 'inline-block',
                                            verticalAlign: 'baseline',
                                            fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            backgroundColor: '#D9E5F7',
                                            color: '#252C34',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            marginRight: '8px',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {reportCode}
                                        </span>
                                    )}
                                    {reportName && <span>{reportName}</span>}
                                </div>
                            )}
                            {eupName && <div style={{ marginBottom: '4px' }}><strong>MPD:</strong> {eupName}</div>}
                            {additionalInfo && <div style={{ marginTop: '8px', fontStyle: 'italic' }}>{additionalInfo}</div>}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className={styles.root}>
                {/*<div className={styles.spacing} />*/}
                <div className={styles.progressContainer}>
                    <div className={styles.successIcon}>✓</div>
                    <div className={styles.progressSuccessMessage}>{progressMessage}</div>
                    {autoReturnDelay > 0 && countdown > 0 && (
                        <div className={styles.autoReturnMessage}>
                            Powrót za {countdown} sekund...
                        </div>
                    )}
                    <button
                        className={styles.backButton}
                        onClick={handleManualReturn}
                    >
                        Powrót teraz
                    </button>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.root}>
                {/*<div className={styles.spacing} />*/}
                <div className={styles.progressContainer}>
                    <Warning48Filled className={styles.iconError} />
                    <div className={styles.progressErrorMessage}>{error}</div>
                    <button
                        className={styles.backButton}
                        onClick={handleManualReturn}
                    >
                        Powrót
                    </button>
                </div>
            </div>
        );
    }
    return (
        <>
            {/* Dialog potwierdzenia */}
            <Dialog open={dialogOpen}>
                <DialogSurface>
                    <DialogTitle>{dialogTitle}</DialogTitle>
                    <DialogBody>
                        {dialogMessage}
                    </DialogBody>
                    <DialogActions>
                        <Button
                            appearance="secondary"
                            onClick={() => handleDialogConfirm(false)}
                        >
                            Nie
                        </Button>
                        <Button
                            appearance="primary"
                            onClick={() => handleDialogConfirm(true)}
                        >
                            Tak
                        </Button>
                    </DialogActions>
                </DialogSurface>
            </Dialog>
        </>
    );
};

export default ProgressPage;
